import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import {
  FiUser, FiMail, FiMapPin, FiLoader,
  FiCheck, FiX, FiSend, FiEye, FiClock,
  FiGrid, FiTarget, FiHash, FiCreditCard,
  FiMessageSquare, FiChevronDown, FiChevronRight,
  FiAlertCircle, FiEdit2, FiSave,
  FiAward, FiRepeat, FiSlash
} from 'react-icons/fi'
import AssignLeaderModal from './AssignLeaderModal'
import ConfirmDialog from './ConfirmDialog'
import { LeaderStageControl, type StageReply } from './LeaderStageControl'
import { LeaderActionsBar } from './LeaderActionsBar'

// Lead encaminhado a esta líder (seção "Leads encaminhados" da sidebar).
// Espelha o item de /api/leader-followups/by-leader.
interface LeaderLeadItem {
  assignmentId: string; conversationId: string; deviceId: string; leadName: string; leadPhone: string
  assignedAt: string | null; deadlineAt: string | null; hoursLeft: number | null
  status: 'open' | 'acknowledged' | 'overdue' | 'escalated' | 'concluded'
  leaderOutcome: 'success' | 'failure' | 'trying' | null; leaderOutcomeReason: string | null
  convertedOracle: boolean; nudgeCount: number; notes: string | null
  nextNudgeAt: string | null; deadlineNotifiedAt: string | null; autoExpiry: boolean
  // Funil v2 (3 etapas) — stage NULL = fluxo antigo/legado
  stage: number | null
  contacted: 'yes' | 'pending' | 'unreachable' | null
  explained: 'yes' | 'partial' | 'refused' | null
  interest: 'yes' | 'thinking' | 'no' | null
}
// Próxima ação automática do lead aguardando: lembrete (12h) → aviso de prazo (24h)
// → expirado. Espelha o sweeper. Mesma lógica do painel Líderes (LeaderFollowupPanel).
const fmtDur = (ms: number) => { const h = ms / 3600000; return h >= 1 ? `${Math.round(h)}h` : `${Math.max(1, Math.round(h * 60))}min` }
function nextLeadAction(l: LeaderLeadItem): { text: string; cls: string } | null {
  if (l.status !== 'open' && l.status !== 'overdue') return null
  const now = Date.now()
  const nudgeMs = l.nextNudgeAt ? new Date(l.nextNudgeAt).getTime() : null
  const deadlineMs = l.deadlineAt ? new Date(l.deadlineAt).getTime() : null
  if (l.nudgeCount === 0 && nudgeMs && nudgeMs > now) {
    const d = nudgeMs - now
    return { text: `lembrete em ${fmtDur(d)}`, cls: d < 3 * 3600000 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400' }
  }
  if (deadlineMs && deadlineMs > now) {
    const d = deadlineMs - now
    return { text: `aviso de prazo em ${fmtDur(d)}`, cls: d < 6 * 3600000 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400' }
  }
  return { text: l.deadlineNotifiedAt ? 'prazo expirado · líder avisada' : 'prazo expirado', cls: 'text-red-600 dark:text-red-400' }
}
const LEAD_STATUS_META: Record<LeaderLeadItem['status'], { label: string; cls: string }> = {
  open:         { label: 'Aguardando', cls: 'bg-sky-500/15 text-sky-600 dark:text-sky-400' },
  acknowledged: { label: 'Em contato', cls: 'bg-amber-500/15 text-amber-600 dark:text-amber-400' },
  overdue:      { label: 'Atrasado',   cls: 'bg-red-500/15 text-red-600 dark:text-red-400' },
  escalated:    { label: 'Escalado',   cls: 'bg-red-600/20 text-red-700 dark:text-red-300' },
  concluded:    { label: 'Concluído',  cls: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
}

interface ContactProfile {
  phone: string
  name: string
  type: string
  code: string | null
  cpf: string | null
  email: string | null
  isRegistered: boolean
  isRegisteredInOracle: boolean
  isActive: boolean
  dataSource: 'oracle' | 'mysql' | 'whatsapp'
  activeWeek: string | null
  status: string | null
  hierarchy: {
    district: string; districtCode: string
    group: string; groupCode: string
  } | null
  location: {
    address: string; neighborhood: string
    city: string; zip: string
    lat: number | null; lng: number | null
  } | null
  crm: {
    project_id: string; project_name: string; project_color: string
    column_title: string; column_color: string
    crm_status: string; estimated_value: number | null
    priority: string | null; notes: string | null
    conversation_id: string
  }[]
  campaigns: {
    templateName: string; status: string; campaignStatus: string
    sentAt: string | null; deliveredAt: string | null; readAt: string | null
    error: string | null; date: string
  }[]
  messages: {
    total: number; fromContact: number; fromUs: number
    firstMessage: string | null; lastMessage: string | null
  }
  conversations: {
    id: string; deviceId: string; botActive: boolean
    botStatus: string; archived: boolean; lastMessageAt: string
  }[]
}

interface ContactProfilePanelProps {
  phone: string | null
  contactName?: string
  conversationId?: string
  // Estado de bloqueio da conversa aberta (vem enriquecido do backend). Passado
  // por prop em vez de buscado aqui: o inbox já tem o dado, e a sidebar não
  // precisa de mais um request por conversa aberta.
  blocked?: { reason: string | null; blocked_at: string | null; blocked_by: string | null } | null
  // Avisa o inbox pra refazer a lista depois de bloquear (badge vermelho +
  // bot desligado só aparecem depois que a conversa é recarregada). Leva o
  // motivo junto pro badge já nascer com o tooltip certo, sem esperar o refetch.
  onBlockChange?: (reason: string) => void
  // NEU-LAB: quando true, aplica o tema neumórfico às superfícies neutras e
  // neutraliza os tints decorativos (mantém só vermelho de perigo/atraso e roxo
  // de "Convertido"). Vem do WhatsAppInboxPanel (?neu=1). Default false.
  neu?: boolean
}

// Cadastro irmão — outra pessoa cuja celular/tel1/tel2 bate com este número.
// Caso real 2026-05-26: 3 pessoas dividindo (67) 99946-7926 (família).
interface PhoneCadastro {
  codigo: string
  nome: string
  tipo_consultor: string
  status: string
  source_field: 'celular' | 'telefone1' | 'telefone2'
  is_canonical: boolean
}
interface PhoneCadastrosResponse {
  matches: PhoneCadastro[]
  canonical: { codigo: string; nome: string; tipo_consultor: string; source_field: string } | null
  shared: boolean
}

// Cores canônicas alinhadas com o filter da inbox (WhatsAppInboxPanel:2065-2069)
const TYPE_COLORS: Record<string, string> = {
  'Consultora': 'bg-sky-500',
  'Líder': 'bg-purple-500',
  'Empresário': 'bg-amber-500',
  'Ger. Distribuição': 'bg-stone-500',
  'Novo Lead': 'bg-emerald-500',
  'Contato': 'bg-gray-500',
}

const TYPE_LABELS: Record<string, string> = {
  'Consultora': 'Consultora',
  'Líder': 'Líder',
  'Empresário': 'Empresário',
  'Ger. Distribuição': 'Ger. Distribuição',
  'Novo Lead': 'Novo Lead',
  'Contato': 'Contato',
}

// Metadata interna gravada dentro do JSON lead_extracted_data pelo bot
// (server.cjs:11257,11389). Não é dado do lead — não exibir.
const EXTRACTED_META_KEYS = new Set(['updated_at', 'created_at', 'version', 'source', 'origem'])

const EXTRACTED_LABELS: Record<string, string> = {
  nome: 'Nome', name: 'Nome',
  cpf: 'CPF',
  cep: 'CEP',
  telefone: 'Telefone', phone: 'Telefone', celular: 'Celular',
  email: 'Email', mail: 'Email',
  endereco: 'Endereço', address: 'Endereço',
  numero: 'Número', complemento: 'Complemento',
  bairro: 'Bairro',
  cidade: 'Cidade', city: 'Cidade',
  estado: 'Estado', uf: 'UF',
  data_nascimento: 'Nascimento', nascimento: 'Nascimento',
  interests: 'Interesses', interesses: 'Interesses',
  motivation: 'Motivação', motivacao: 'Motivação',
  profissao: 'Profissão', profession: 'Profissão',
}

const formatExtractedLabel = (k: string): string => {
  const low = k.toLowerCase()
  if (EXTRACTED_LABELS[low]) return EXTRACTED_LABELS[low]
  const spaced = k.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/

const formatExtractedValue = (k: string, v: any): string => {
  if (v === null || v === undefined || v === '') return ''
  // Arrays vazios → vazio; arrays com itens → "a · b · c"
  if (Array.isArray(v)) {
    const items = v
      .filter(x => x !== null && x !== undefined && x !== '')
      .map(x => typeof x === 'string' || typeof x === 'number' ? String(x) : JSON.stringify(x))
    return items.length === 0 ? '' : items.join(' · ')
  }
  const raw = typeof v === 'string' || typeof v === 'number' ? String(v) : JSON.stringify(v)
  if (!raw.trim()) return ''
  const low = k.toLowerCase()
  if (low === 'cpf') {
    const d = raw.replace(/\D/g, '')
    if (d.length === 11) return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`
  }
  if (low === 'cep') {
    const d = raw.replace(/\D/g, '')
    if (d.length === 8) return `${d.slice(0,5)}-${d.slice(5)}`
  }
  if (low === 'telefone' || low === 'celular' || low === 'phone') {
    const d = raw.replace(/\D/g, '')
    if (d.length === 13 && d.startsWith('55')) return `(${d.slice(2,4)}) ${d.slice(4,9)}-${d.slice(9)}`
    if (d.length === 12 && d.startsWith('55')) return `(${d.slice(2,4)}) ${d.slice(4,8)}-${d.slice(8)}`
    if (d.length === 11) return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`
    if (d.length === 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`
  }
  if (typeof v === 'string' && ISO_DATETIME_RE.test(v)) {
    const dt = new Date(v)
    if (!isNaN(dt.getTime())) {
      return dt.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
    }
  }
  return raw
}

// Humanização de intent.type (vem em JSON {"type":"...","detail":"..."} —
// server grava livre, snake_case em inglês ou português). Valores observados
// no banco em 2026-05-18 incluem low_engagement, cadastro_consultora,
// pagamento_pendente, information_seeking etc.
const INTENT_LABELS: Record<string, string> = {
  'tráfego_pago': 'Tráfego pago',
  'trafego_pago': 'Tráfego pago',
  'intent_detectado': 'Detecção semântica',
  'midia_inicial': 'Mídia inicial (não-texto)',
  'low_engagement': 'Baixo engajamento',
  'cadastro_consultora': 'Cadastro de consultora',
  'consultora': 'Cadastro de consultora',
  'pagamento_pendente': 'Pagamento pendente',
  'pagamento_cobranca': 'Cobrança',
  'pagamento': 'Pagamento',
  'pagamento_confirmado': 'Pagamento confirmado',
  'confirmacao_pagamento': 'Confirmação de pagamento',
  'information_seeking': 'Buscando informações',
  'information_gathering': 'Aguardando dados do lead',
  'lider_operacional': 'Líder operacional',
  'lider_ativa_convertendo': 'Líder ativa convertendo',
  'unknown': 'Indefinido',
}

const humanizeSnake = (s: string): string => {
  const spaced = s.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

const formatIntent = (intent: string | null): string => {
  if (!intent) return ''
  const i = intent.trim()
  if (INTENT_LABELS[i]) return INTENT_LABELS[i]
  if (i.startsWith('{') || i.startsWith('[')) {
    try {
      const o = JSON.parse(i)
      if (o && typeof o === 'object') {
        const t = String(o.type || o.detail || o.intent || '')
        if (t) return INTENT_LABELS[t] || humanizeSnake(t)
      }
    } catch { /* cai no fallback */ }
  }
  return humanizeSnake(i)
}

const TEMPERATURE_LABELS: Record<string, string> = {
  cold: 'Fria',
  warm: 'Morna',
  hot: 'Quente',
  closing: 'Em fechamento',
}

const formatTemperature = (t: string | null): string => {
  if (!t) return ''
  return TEMPERATURE_LABELS[t.toLowerCase()] || humanizeSnake(t)
}

type AIStage = { id: string; title: string; status: string; color: string; position: number; isCurrent: boolean; isPassed: boolean }
type AITimeline = {
  entrada: { at: string | null }
  dadosCompletos: { at: string | null }
  enviadoLider: { at: string | null; leaderName: string | null; leaderCode: string | null } | null
  convertido: { at: string | null; nome: string | null; codigo: string | null; tipo: string | null } | null
}
type AISession = {
  conversationId: string
  botActive: boolean
  botStatus: string
  intent: string | null
  temperature: string | null
  leadScore: number | null
  extractedData: Record<string, any>
  currentStage: { id: string; title: string; status: string; color: string } | null
  stages: AIStage[]
  progress: { current: number; total: number }
  timeline?: AITimeline
  updatedAt: string
} | null

export function ContactProfilePanel({ phone, contactName, conversationId, blocked, onBlockChange, neu = false }: ContactProfilePanelProps) {
  const { authenticatedFetch, user } = useAuth()
  // Bloquear contato (golpista) direto da conversa — atalho pra mesma tabela do
  // painel Recrutamento → Bloqueados. Admin-only igual ao painel: a rota
  // POST /api/blocklist é adminMiddleware, então mostrar o controle pra
  // não-admin só renderia um 403 na cara dele.
  const [blockReason, setBlockReason] = useState('')
  const [blocking, setBlocking] = useState(false)
  const [blockError, setBlockError] = useState<string | null>(null)
  const [confirmBlock, setConfirmBlock] = useState(false)
  const [profile, setProfile] = useState<ContactProfile | null>(null)
  const [aiSession, setAiSession] = useState<AISession>(null)
  const [loading, setLoading] = useState(false)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['info', 'ai', 'crm', 'leads']))
  const [editing, setEditing] = useState(false)
  const [editForm, setEditForm] = useState({ nome: '', email: '', celular: '', endereco_cidade: '', endereco_bairro: '', endereco_cep: '' })
  const [saving, setSaving] = useState(false)
  // Cadastros que compartilham o número (família, linha de parente, etc.)
  const [phoneCadastros, setPhoneCadastros] = useState<PhoneCadastrosResponse | null>(null)
  // Tel. 2 do bloco de número compartilhado: colapsado por default (geralmente
  // é a rede da líder — 20+ consultoras com o telefone dela como secundário,
  // pouco útil pro operador). Celular/tel. 1 aparecem direto (são conflito real).
  const [sharedTel2Expanded, setSharedTel2Expanded] = useState(false)
  // Leads encaminhados a ESTA líder (se o contato for líder com leads). Self-gating:
  // busca por profile.code; vazio pra não-líder → seção não aparece. É o fallback
  // manual de baixa quando o fluxo automático (template/nudge) falha.
  const [leaderLeads, setLeaderLeads] = useState<LeaderLeadItem[]>([])
  const [leaderCounts, setLeaderCounts] = useState<{ total: number; pending: number; concluded: number } | null>(null)
  const [leaderLeadsLoading, setLeaderLeadsLoading] = useState(false)
  const [leadActionBusy, setLeadActionBusy] = useState<string | null>(null)
  const [leadError, setLeadError] = useState<string>('')
  const [leadInfo, setLeadInfo] = useState<string>('')
  const [showConcludedLeads, setShowConcludedLeads] = useState(false)
  // Trocar líder: lead a ser reatribuído a outra líder (abre o AssignLeaderModal).
  const [reassignLead, setReassignLead] = useState<LeaderLeadItem | null>(null)

  const loadProfile = useCallback(async (phoneNumber: string) => {
    setLoading(true)
    try {
      const [r, rSession] = await Promise.all([
        authenticatedFetch(`/api/contact-profile/${phoneNumber}`),
        authenticatedFetch(`/api/contacts/${phoneNumber}/ai-session`)
      ])
      const d = await r.json()
      if (d.success) setProfile(d.profile)
      else setProfile(null)
      try {
        const ds = await rSession.json()
        setAiSession(ds?.session || null)
      } catch { setAiSession(null) }
    } catch { setProfile(null); setAiSession(null) }
    finally { setLoading(false) }
  }, [authenticatedFetch])

  useEffect(() => {
    if (phone) {
      const clean = phone.replace(/\D/g, '').replace(/@.*/, '')
      if (clean.length >= 8) loadProfile(clean)
      else { setProfile(null); setAiSession(null) }
    } else {
      setProfile(null)
      setAiSession(null)
    }
  }, [phone, loadProfile])

  // Detectar números compartilhados — endpoint só responde quando a conv tem
  // remote_jid resolvível. Falha silenciosa: badge só renderiza se shared=true.
  useEffect(() => {
    if (!conversationId) { setPhoneCadastros(null); return }
    let cancelled = false
    ;(async () => {
      try {
        const r = await authenticatedFetch(`/api/conversations/${encodeURIComponent(conversationId)}/phone-cadastros`)
        if (!r.ok) { if (!cancelled) setPhoneCadastros(null); return }
        const d: PhoneCadastrosResponse = await r.json()
        if (!cancelled) setPhoneCadastros(d)
      } catch {
        if (!cancelled) setPhoneCadastros(null)
      }
    })()
    return () => { cancelled = true }
  }, [conversationId, authenticatedFetch])

  // Refresh quando AssignLeaderModal completa a atribuição — caso contrário a
  // timeline "Enviado para líder" continuaria "pendente" até trocar de conversa.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail || {}
      const cleanCurrent = (phone || '').replace(/\D/g, '').replace(/@.*/, '')
      const cleanEvent = String(detail.phone || '').replace(/\D/g, '')
      // Refresh se o evento é da conversa atual (ou se phone não foi enviado)
      if (!detail.phone || cleanEvent === cleanCurrent) {
        if (cleanCurrent.length >= 8) loadProfile(cleanCurrent)
      }
    }
    window.addEventListener('leaderAssigned', handler)
    return () => window.removeEventListener('leaderAssigned', handler)
  }, [phone, loadProfile])

  const toggleSection = (s: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev)
      if (next.has(s)) next.delete(s); else next.add(s)
      return next
    })
  }

  // Manda o remote_jid cru — o backend (blockKeyFromPhone) come o
  // "@s.whatsapp.net" e canonicaliza. Não normalizar aqui evita duas verdades.
  const blockContact = async () => {
    if (!phone || !blockReason.trim()) return
    setBlocking(true)
    setBlockError(null)
    try {
      const r = await authenticatedFetch('/api/blocklist', {
        method: 'POST',
        body: JSON.stringify({ phone, name: contactName || null, reason: blockReason }),
      })
      const d = await r.json()
      if (!r.ok || !d.success) throw new Error(d.error || `HTTP ${r.status}`)
      const usada = blockReason.trim()
      setBlockReason('')
      onBlockChange?.(usada)
    } catch (e) {
      setBlockError((e as Error).message)
    } finally {
      setBlocking(false)
    }
  }

  // Seções COBRANÇA e FINANCEIRO removidas em 2026-07-10: essa visão pertence
  // ao fin.twms (painel Cobrança/Conversas de lá) — aqui só duplicava e pesava
  // no Oracle. A rota /api/cobranca/cliente segue viva (o fin proxeia pra ela).

  // Busca os leads encaminhados a esta líder. Reusado pelo effect e pelos refresh
  // pós-ação. profile.code é o codigo do cadastro = leader_code nas atribuições.
  const loadLeaderLeads = useCallback(async (code: string) => {
    setLeaderLeadsLoading(true)
    try {
      const r = await authenticatedFetch(`/api/leader-followups/by-leader?leaderCode=${encodeURIComponent(code)}`)
      const d = await r.json()
      if (r.ok && d.success) { setLeaderLeads(d.items || []); setLeaderCounts(d.counts || null) }
      else { setLeaderLeads([]); setLeaderCounts(null) }
    } catch { setLeaderLeads([]); setLeaderCounts(null) }
    finally { setLeaderLeadsLoading(false) }
  }, [authenticatedFetch])

  // Ao trocar de contato: reseta e (se há code) busca.
  useEffect(() => {
    const code = profile?.code
    setLeadError(''); setShowConcludedLeads(false)
    if (!code) { setLeaderLeads([]); setLeaderCounts(null); return }
    loadLeaderLeads(code)
  }, [profile?.code, loadLeaderLeads])

  // POST de ação manual (outcome|note) + refresh. authMiddleware no backend.
  const leadAction = useCallback(async (assignmentId: string, path: string, body: any) => {
    setLeadActionBusy(assignmentId); setLeadError(''); setLeadInfo('')
    try {
      const r = await authenticatedFetch(`/api/leader-followups/${encodeURIComponent(assignmentId)}/${path}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok || d.success === false) throw new Error(d.error || 'Erro ao salvar')
      // Cobrança/aviso entram numa FILA throttada (3 por líder a cada 2 min) — não vão
      // na hora. Feedback explícito senão o operador acha que não funcionou e re-clica.
      if (path === 'resend-nudge' || path === 'notify-expired') {
        setLeadInfo(d.alreadyQueued ? 'Já estava na fila.'
          : path === 'notify-expired' ? 'Aviso de prazo enfileirado — sai em instantes.'
          : 'Cobrança enfileirada — sai em instantes (3 por líder a cada 2 min).')
        setTimeout(() => setLeadInfo(''), 5000)
      }
      if (profile?.code) await loadLeaderLeads(profile.code)
    } catch (e) {
      setLeadError(e instanceof Error ? e.message : 'Erro')
    } finally {
      setLeadActionBusy(null)
    }
  }, [authenticatedFetch, profile?.code, loadLeaderLeads])

  const startEditing = () => {
    setEditForm({
      nome: profile?.name || '',
      email: profile?.email || '',
      celular: phone?.replace(/\D/g, '').replace(/@.*/, '') || '',
      endereco_cidade: profile?.location?.city || '',
      endereco_bairro: profile?.location?.neighborhood || '',
      endereco_cep: profile?.location?.zip || '',
    })
    setEditing(true)
  }

  const saveEdit = async () => {
    setSaving(true)
    try {
      const cleanPhone = phone?.replace(/\D/g, '').replace(/@.*/, '') || ''
      await authenticatedFetch('/api/crm/cadastro-cliente/upsert', {
        method: 'POST',
        body: JSON.stringify({
          phone: cleanPhone,
          nome: editForm.nome,
          email: editForm.email,
          endereco_cidade: editForm.endereco_cidade,
          endereco_bairro: editForm.endereco_bairro,
          endereco_cep: editForm.endereco_cep,
          // Sem isso o backend não atualiza whatsapp_conversations.remote_name —
          // o nome corrigido ficava só no cadastro e o pushname antigo persistia.
          conversation_id: conversationId,
        })
      })
      setEditing(false)
      // Reload profile
      if (cleanPhone.length >= 8) loadProfile(cleanPhone)
    } catch {}
    finally { setSaving(false) }
  }

  const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-'
  const fmtDateTime = (d: string | null) => d ? new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'
  const fmtCpf = (c: string) => { const d = c.replace(/\D/g, ''); return d.length === 11 ? d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : c }

  if (!phone) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-muted-foreground px-4">
        <FiUser className="w-10 h-10 mb-3 opacity-20" />
        <span className="text-sm font-medium">Perfil do Contato</span>
        <span className="text-[11px] text-center mt-1 opacity-60">Selecione uma conversa para ver o perfil completo</span>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <FiLoader className="w-5 h-5 animate-spin text-muted-foreground" />
        <span className="text-[11px] text-muted-foreground mt-2">Carregando perfil...</span>
      </div>
    )
  }

  const p = profile
  const typeColor = TYPE_COLORS[p?.type || 'Contato'] || 'bg-gray-500'
  const typeLabel = TYPE_LABELS[p?.type || 'Contato'] || p?.type || 'Contato'
  // Prioridade: cadastro Oracle/MySQL > nome extraído pelo bot > pushname > fallback.
  // Quando NÃO há cadastro, o backend faz fallback de profile.name pra
  // remote_name (pushname WhatsApp) — que pode ser branding/apelido tipo
  // "EXCELÊNCIA", "CRBA", "Mhy❤️". Nesse caso o nome extraído pelo bot
  // (resposta direta a "qual seu nome completo?") é mais confiável.
  // Mesma prioridade que a inbox usa (WhatsAppInboxPanel:1959-1985).
  const isPlaceholderName = (s?: string | null) =>
    !s || ['Desconhecido', 'Sem nome', 'Não informado', 'Cliente', 'Lead'].includes(String(s).trim())
  const extractedNome = aiSession?.extractedData?.nome || aiSession?.extractedData?.name
  const registeredName = p?.isRegistered ? p?.name : null
  const pushName = !p?.isRegistered ? p?.name : null
  const displayName =
    (!isPlaceholderName(registeredName) && registeredName) ||
    (extractedNome && String(extractedNome).trim()) ||
    (!isPlaceholderName(pushName) && pushName) ||
    (!isPlaceholderName(contactName) && contactName) ||
    'Desconhecido'

  const campaignStatusIcon = (s: string) => {
    switch (s) { case 'read': return <FiEye className="w-3 h-3 text-violet-500" />; case 'delivered': return <FiCheck className="w-3 h-3 text-emerald-500" />; case 'sent': return <FiSend className="w-2.5 h-2.5 text-blue-500" />; case 'failed': return <FiX className="w-3 h-3 text-red-500" />; default: return <FiClock className="w-3 h-3 text-muted-foreground" /> }
  }

  return (
    /* Fundo transparente de propósito: o vidro vem do wrapper (.glass-panel na
       coluna lg+ e no drawer <lg do WhatsAppInboxPanel). */
    <div className={`h-full w-full flex flex-col overflow-hidden ${neu ? 'neu-profile rounded-2xl' : ''}`}>
      {/* Header com avatar e tipo */}
      <div className={`shrink-0 px-4 py-4 ${neu ? '' : 'border-b border-border'}`}>
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold shrink-0 ${neu ? 'neu-avatar text-foreground' : `${typeColor} text-white`}`}>
            {displayName.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-foreground truncate">{displayName.toUpperCase()}</div>
            <div className="text-[10px] text-muted-foreground">{(() => {
              const d = (phone || '').replace(/\D/g, '').replace(/@.*/, '')
              if (d.length === 13 && d.startsWith('55')) return `(${d.slice(2,4)}) ${d.slice(4,9)}-${d.slice(9)}`
              if (d.length === 12 && d.startsWith('55')) return `(${d.slice(2,4)}) ${d.slice(4,8)}-${d.slice(8)}`
              if (d.length === 11) return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`
              if (d.length === 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`
              return d
            })()}</div>
          </div>
          <button onClick={editing ? saveEdit : startEditing} disabled={saving}
            className={`p-1.5 rounded-lg transition-colors shrink-0 ${editing ? 'bg-emerald-500/15 text-emerald-500 hover:bg-emerald-500/25' : 'text-muted-foreground/40 hover:text-foreground hover:bg-secondary'}`}
            title={editing ? 'Salvar' : 'Editar contato'}>
            {saving ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : editing ? <FiSave className="w-3.5 h-3.5" /> : <FiEdit2 className="w-3.5 h-3.5" />}
          </button>
        </div>
        {/* Badges */}
        <div className="flex flex-wrap gap-1.5 mt-3">
          <span className={`text-[9px] px-2 py-0.5 rounded-full font-medium ${neu ? 'neu-chip' : `text-white ${typeColor}`}`}>
            {typeLabel}
          </span>
          {p?.isRegisteredInOracle && (
            <span className={`text-[9px] px-2 py-0.5 rounded-full font-medium ${neu ? 'neu-chip' : 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'}`}>
              DRACO
            </span>
          )}
          {p?.isRegistered && !p?.isRegisteredInOracle && (
            <span className={`text-[9px] px-2 py-0.5 rounded-full font-medium ${neu ? 'neu-chip' : 'bg-blue-500/15 text-blue-600 dark:text-blue-400'}`}>
              Cadastrado
            </span>
          )}
          {!p?.isRegistered && (
            <span className={`text-[9px] px-2 py-0.5 rounded-full font-medium ${neu ? 'neu-chip' : 'bg-amber-500/15 text-amber-600 dark:text-amber-400'}`}>
              Não cadastrado
            </span>
          )}
          {p?.isActive && (
            <span className={`text-[9px] px-2 py-0.5 rounded-full font-medium ${neu ? 'neu-chip' : 'bg-blue-500/15 text-blue-600 dark:text-blue-400'}`}>
              Ativa {p.activeWeek}
            </span>
          )}
          {p?.isRegistered && !p?.isActive && (
            <span className="text-[9px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-600 dark:text-red-400 font-medium">
              Inativa
            </span>
          )}
        </div>

        {/* Aviso de número compartilhado entre múltiplos cadastros. Padrão típico:
            1 dono real (DONA, celular) + 0-2 "co-shares" reais (celular/tel.1 de
            outras pessoas — família, sócio, mesma linha) + N consultoras com o
            número como tel.2 (rede da líder/empresário — ruído, alto volume).
            UI prioriza DONA + co-shares; tel.2 colapsa pra não enterrar o sinal. */}
        {phoneCadastros?.shared && phoneCadastros.matches.length > 1 && (() => {
          const typeLbl = (t: string) =>
            t === '1' ? 'Empresária' : t === '2' ? 'Líder' : t === '3' ? 'Consultora' : `tipo ${t}`;
          const fieldLbl = (f: string) =>
            f === 'celular' ? 'celular' : f === 'telefone1' ? 'tel. 1' : 'tel. 2';
          const all = phoneCadastros.matches;
          const dona = all.find(m => m.is_canonical) || null;
          const primary = all.filter(m => !m.is_canonical && m.source_field !== 'telefone2');
          const tel2 = all.filter(m => !m.is_canonical && m.source_field === 'telefone2');
          const summaryBits: string[] = [];
          const cCel = primary.filter(m => m.source_field === 'celular').length;
          const cTel1 = primary.filter(m => m.source_field === 'telefone1').length;
          if (cCel) summaryBits.push(`${cCel} celular`);
          if (cTel1) summaryBits.push(`${cTel1} tel. 1`);
          if (tel2.length) summaryBits.push(`${tel2.length} tel. 2`);
          return (
            <div className="mt-3 p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg text-[10px]">
              <div
                className="font-semibold text-amber-600 dark:text-amber-400 mb-1 flex items-center gap-1"
                title={`Mesmo número aparece em ${all.length} cadastros do Oracle DRACO. Ordem: DONA (canônico) > celular > tel. 1 > tel. 2.`}
              >
                📞 {all.length} cadastros · {summaryBits.join(' + ')}
              </div>

              {dona && (
                <div className="text-foreground" title="Cadastro canônico (DONA) — quem o sistema considera o titular real do número">
                  <span className="font-semibold">{dona.nome}</span>
                  <span className="opacity-60">
                    {' · '}{typeLbl(dona.tipo_consultor)}{' · '}{fieldLbl(dona.source_field)}
                    {' · '}<span className="font-medium text-amber-600 dark:text-amber-400">DONA</span>
                  </span>
                </div>
              )}

              {primary.length > 0 && (
                <ul className="mt-0.5 space-y-0.5 text-muted-foreground">
                  {primary.map(m => (
                    <li key={m.codigo} title="Compartilha o número como contato principal — possível co-titular (família, sócio, mesma linha)">
                      {m.nome}
                      <span className="opacity-60">
                        {' · '}{typeLbl(m.tipo_consultor)}{' · '}{fieldLbl(m.source_field)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {tel2.length > 0 && (
                <div className="mt-1 pt-1 border-t border-amber-500/20">
                  <button
                    type="button"
                    onClick={() => setSharedTel2Expanded(v => !v)}
                    className="w-full text-left text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                    title="Cadastros que registraram este número como telefone secundário (tel. 2). Normalmente é a rede da líder/empresário — clique pra ver."
                  >
                    {sharedTel2Expanded ? <FiChevronDown className="w-3 h-3" /> : <FiChevronRight className="w-3 h-3" />}
                    <span>{tel2.length} como tel. 2</span>
                    <span className="opacity-50">(rede)</span>
                  </button>
                  {sharedTel2Expanded && (
                    <p className="mt-1 ml-4 leading-relaxed text-muted-foreground">
                      {tel2.map(m => m.nome).join(' · ')}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Seções expandíveis */}
      <div className={`flex-1 overflow-y-auto${neu ? ' px-2 pt-2 pb-3 rounded-b-2xl' : ''}`}>
        {/* ─── INFO ─── */}
        <button onClick={() => toggleSection('info')} className="w-full flex items-center gap-2 px-4 py-2 text-[11px] font-semibold text-muted-foreground hover:bg-secondary/50 transition-colors border-b border-border/50">
          {expandedSections.has('info') ? <FiChevronDown className="w-3 h-3" /> : <FiChevronRight className="w-3 h-3" />}
          <FiUser className="w-3 h-3" /> INFORMAÇÕES
        </button>
        {expandedSections.has('info') && (
          <div className="px-4 py-2 space-y-1.5 border-b border-border/50">
            {/* Modo edição */}
            {editing ? (
              <div className="space-y-2">
                <div>
                  <label className="text-[9px] text-muted-foreground uppercase tracking-wider">Nome</label>
                  <input value={editForm.nome} onChange={e => setEditForm(f => ({ ...f, nome: e.target.value }))}
                    className="w-full text-[11px] px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-violet-500" />
                </div>
                <div>
                  <label className="text-[9px] text-muted-foreground uppercase tracking-wider">Email</label>
                  <input value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full text-[11px] px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-violet-500" />
                </div>
                <div>
                  <label className="text-[9px] text-muted-foreground uppercase tracking-wider">Cidade</label>
                  <input value={editForm.endereco_cidade} onChange={e => setEditForm(f => ({ ...f, endereco_cidade: e.target.value }))}
                    className="w-full text-[11px] px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-violet-500" />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-[9px] text-muted-foreground uppercase tracking-wider">Bairro</label>
                    <input value={editForm.endereco_bairro} onChange={e => setEditForm(f => ({ ...f, endereco_bairro: e.target.value }))}
                      className="w-full text-[11px] px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-violet-500" />
                  </div>
                  <div className="w-24">
                    <label className="text-[9px] text-muted-foreground uppercase tracking-wider">CEP</label>
                    <input value={editForm.endereco_cep} onChange={e => setEditForm(f => ({ ...f, endereco_cep: e.target.value }))}
                      className="w-full text-[11px] px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-violet-500" />
                  </div>
                </div>
                <button onClick={() => setEditing(false)}
                  className="w-full text-[10px] text-muted-foreground hover:text-foreground py-1 transition-colors">
                  Cancelar
                </button>
              </div>
            ) : (
            <>
            {p?.code && (
              <div className="flex items-center gap-2 text-[11px]">
                <FiHash className="w-3 h-3 text-muted-foreground shrink-0" />
                <span className="text-muted-foreground">Código:</span>
                <span className="text-foreground font-medium">{p.code}</span>
              </div>
            )}
            {p?.cpf && (
              <div className="flex items-center gap-2 text-[11px]">
                <FiCreditCard className="w-3 h-3 text-muted-foreground shrink-0" />
                <span className="text-muted-foreground">CPF:</span>
                <span className="text-foreground font-medium">{fmtCpf(p.cpf)}</span>
              </div>
            )}
            {p?.email && (
              <div className="flex items-center gap-2 text-[11px]">
                <FiMail className="w-3 h-3 text-muted-foreground shrink-0" />
                <span className="text-foreground truncate">{p.email}</span>
              </div>
            )}
            {p?.hierarchy && (
              <>
                <div className="flex items-center gap-2 text-[11px]">
                  <FiGrid className="w-3 h-3 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground">Grupo:</span>
                  <span className="text-foreground font-medium truncate">{p.hierarchy.group}</span>
                </div>
                <div className="flex items-center gap-2 text-[11px]">
                  <FiTarget className="w-3 h-3 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground">Distrito:</span>
                  <span className="text-foreground font-medium truncate">{p.hierarchy.district}</span>
                </div>
              </>
            )}
            {p?.location?.city && (
              <div className="flex items-center gap-2 text-[11px]">
                <FiMapPin className="w-3 h-3 text-muted-foreground shrink-0" />
                <span className="text-foreground truncate">{p.location.city}{p.location.zip ? ` - ${p.location.zip}` : ''}</span>
              </div>
            )}
            {/* Mensagens stats */}
            {p?.messages && p.messages.total > 0 && (
              <div className="flex items-center gap-2 text-[11px] pt-1">
                <FiMessageSquare className="w-3 h-3 text-muted-foreground shrink-0" />
                <span className="text-muted-foreground">{p.messages.total} msgs</span>
                <span className="text-[9px] text-muted-foreground/60">({p.messages.fromContact} recebidas, {p.messages.fromUs} enviadas)</span>
              </div>
            )}
            {p?.messages?.firstMessage && (
              <div className="text-[10px] text-muted-foreground/60 pl-5">
                Primeiro contato: {fmtDate(p.messages.firstMessage)}
              </div>
            )}
            </>
            )}
          </div>
        )}

        {/* Seção CRM removida em 2026-05-15 junto com o painel CRM.
            Os dados em p.crm continuam sendo populados pelo backend (vêm de
            bot_conversation_state via JOIN) — preservados para o bot Juliane
            e possível UI futura. */}

        {/* ─── ATENDIMENTO COM IA ─── */}
        {aiSession && (
          <>
            <button onClick={() => toggleSection('ai')} className="w-full flex items-center gap-2 px-4 py-2 text-[11px] font-semibold text-muted-foreground hover:bg-secondary/50 transition-colors border-b border-border/50">
              {expandedSections.has('ai') ? <FiChevronDown className="w-3 h-3" /> : <FiChevronRight className="w-3 h-3" />}
              <span className={`w-2 h-2 rounded-full ${aiSession.botActive ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} />
              ATENDIMENTO COM IA
              {aiSession.currentStage && (
                <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: `color-mix(in srgb, ${aiSession.currentStage.color} 15%, transparent)`, color: aiSession.currentStage.color }}>
                  {aiSession.currentStage.title}
                </span>
              )}
            </button>
            {expandedSections.has('ai') && (
              <div className="px-4 py-3 space-y-2 border-b border-border/50">
                {/* Marcos do lead — substitui "Status: Bot atendendo" (já no badge do header).
                    4 eventos: entrada, dados completos, enviado pra líder, convertido.
                    Cada um com ✓ + data quando ocorreu, ◯ + "pendente" quando não. */}
                {aiSession.timeline && (
                  <div className="space-y-1">
                    {([
                      { key: 'entrada',         label: 'Data de entrada',  at: aiSession.timeline.entrada?.at,         sub: null },
                      { key: 'dadosCompletos',  label: 'Dados completos',  at: aiSession.timeline.dadosCompletos?.at,  sub: null },
                      { key: 'enviadoLider',    label: 'Enviado para líder', at: aiSession.timeline.enviadoLider?.at ?? null,
                        sub: aiSession.timeline.enviadoLider?.leaderName ? `${aiSession.timeline.enviadoLider.leaderName}${aiSession.timeline.enviadoLider.leaderCode ? ` (${aiSession.timeline.enviadoLider.leaderCode})` : ''}` : null },
                      { key: 'convertido',      label: 'Convertido',       at: aiSession.timeline.convertido?.at ?? null,
                        sub: aiSession.timeline.convertido?.nome ? `${aiSession.timeline.convertido.nome}${aiSession.timeline.convertido.codigo ? ` (${aiSession.timeline.convertido.codigo})` : ''}` : null },
                    ] as const).map((evt) => {
                      const done = !!evt.at
                      // convertido sem timestamp mas com cadastro: mostra ✓ sem data (Oracle ainda não preencheu converted_consultant_at)
                      const partial = !done && evt.key === 'convertido' && !!aiSession.timeline?.convertido
                      return (
                        <div key={evt.key} className="flex items-start gap-2 text-[11px]">
                          <span className={`mt-[3px] inline-flex items-center justify-center w-3.5 h-3.5 rounded-full shrink-0 ${
                            done ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400' :
                            partial ? 'bg-amber-500/20 text-amber-600 dark:text-amber-400' :
                            'bg-muted text-muted-foreground/60'
                          }`}>
                            {done || partial ? (
                              <svg className="w-2.5 h-2.5" viewBox="0 0 12 12" fill="none">
                                <path d="M2.5 6.5L5 9L9.5 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            ) : (
                              <span className="w-1.5 h-1.5 rounded-full bg-current opacity-50" />
                            )}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className={done || partial ? 'text-foreground font-medium' : 'text-muted-foreground'}>{evt.label}</span>
                              <span className={`tabular-nums shrink-0 text-[10px] ${
                                done ? 'text-muted-foreground' : partial ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground/60'
                              }`}>
                                {done && evt.at
                                  ? new Date(evt.at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
                                  : partial ? 'sem data' : 'pendente'}
                              </span>
                            </div>
                            {evt.sub && (
                              <div className="text-[10px] text-muted-foreground truncate">{evt.sub}</div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
                {aiSession.intent && (
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-muted-foreground">Origem:</span>
                    <span className="font-medium text-foreground truncate ml-2" title={aiSession.intent}>
                      {formatIntent(aiSession.intent)}
                    </span>
                  </div>
                )}
                {aiSession.temperature && (
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-muted-foreground">Temperatura:</span>
                    <span className="font-medium text-foreground">{formatTemperature(aiSession.temperature)}</span>
                  </div>
                )}

                {/* Dados coletados pelo bot — metadata interna (updated_at,
                    created_at, version, _*) é filtrada porque o backend grava
                    `updated_at: new Date().toISOString()` dentro do JSON
                    lead_extracted_data (server.cjs:11257). Labels/valores
                    humanizados (CPF, CEP, telefone, ISO datetime, arrays).
                    Dedup por label canônico — bot às vezes grava `nome` E
                    `name` (ou `cidade` E `city`); o primeiro com valor real
                    vence pra não duplicar linha "Nome:". Linhas com valor
                    vazio são omitidas. */}
                {(() => {
                  const groups: Record<string, { key: string; label: string; value: string }> = {}
                  for (const [k, v] of Object.entries(aiSession.extractedData || {})) {
                    if (EXTRACTED_META_KEYS.has(k.toLowerCase()) || k.startsWith('_')) continue
                    const value = formatExtractedValue(k, v)
                    if (!value) continue
                    const label = formatExtractedLabel(k)
                    if (!groups[label]) groups[label] = { key: k, label, value }
                  }
                  const entries = Object.values(groups)
                  if (entries.length === 0) return null
                  return (
                    <div className="pt-2 border-t border-border/40">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1">Dados coletados</div>
                      <div className="space-y-0.5">
                        {entries.map(({ key, label, value }) => (
                          <div key={key} className="flex items-start gap-2 text-[10px]">
                            <span className="text-muted-foreground shrink-0">{label}:</span>
                            <span className="text-foreground break-words flex-1 min-w-0">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })()}
              </div>
            )}
          </>
        )}

        {/* ─── CAMPANHAS ─── */}
        <button onClick={() => toggleSection('campaigns')} className="w-full flex items-center gap-2 px-4 py-2 text-[11px] font-semibold text-muted-foreground hover:bg-secondary/50 transition-colors border-b border-border/50">
          {expandedSections.has('campaigns') ? <FiChevronDown className="w-3 h-3" /> : <FiChevronRight className="w-3 h-3" />}
          <FiSend className="w-3 h-3" /> CAMPANHAS
          {(p?.campaigns?.length || 0) > 0 && <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded-full ${neu ? 'neu-chip' : 'bg-blue-500/15 text-blue-500'}`}>{p!.campaigns.length}</span>}
        </button>
        {expandedSections.has('campaigns') && (
          <div className="px-4 py-2 space-y-1.5 border-b border-border/50">
            {(p?.campaigns?.length || 0) === 0 ? (
              <div className="text-[11px] text-muted-foreground/60 text-center py-2">
                Nenhuma campanha enviada
              </div>
            ) : (
              p!.campaigns.map((c, i) => {
                const st = c.status
                const stColor = st === 'read' ? 'text-violet-500' : st === 'delivered' ? 'text-emerald-500' : st === 'sent' ? 'text-blue-500' : st === 'failed' ? 'text-red-500' : 'text-muted-foreground'
                const stLabel = st === 'read' ? 'Leu' : st === 'delivered' ? 'Recebeu' : st === 'sent' ? 'Enviado' : st === 'failed' ? 'Falhou' : 'Pendente'
                const stBg = st === 'read' ? 'bg-violet-500/10' : st === 'delivered' ? 'bg-emerald-500/10' : st === 'sent' ? 'bg-blue-500/10' : st === 'failed' ? 'bg-red-500/10' : 'bg-muted/30'
                const fmtFull = (d: string | null) => d ? new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : null
                const tipSent = c.sentAt ? `Enviado em ${fmtFull(c.sentAt)}` : 'Aguardando envio'
                const tipDelivered = c.deliveredAt ? `Entregue em ${fmtFull(c.deliveredAt)}` : 'Aguardando entrega'
                const tipRead = c.readAt ? `Lido em ${fmtFull(c.readAt)}` : 'Não lido ainda'
                return (
                  <div key={i} className={`p-2 ${neu ? 'neu-card' : `rounded-lg ${stBg} border border-border/30`}`}>
                    <div className="flex items-center gap-1.5">
                      {campaignStatusIcon(st)}
                      <span className="text-[10px] font-medium text-foreground truncate flex-1">{c.templateName}</span>
                      {c.error && <FiAlertCircle className="w-3 h-3 text-red-400 shrink-0" title={c.error} />}
                    </div>
                    <div className="flex items-center gap-2 mt-1 ml-5">
                      <span className={`text-[9px] font-medium ${stColor}`}>{stLabel}</span>
                      <span className="text-[9px] text-muted-foreground/50">•</span>
                      <span className="text-[9px] text-muted-foreground">{fmtDateTime(c.sentAt || c.date)}</span>
                    </div>
                    {/* Timeline de entrega */}
                    <div className="flex items-center gap-1 mt-1.5 ml-5">
                      <div className={`w-1.5 h-1.5 rounded-full ${c.sentAt ? 'bg-blue-500' : 'bg-muted-foreground/20'}`} title={tipSent} />
                      <div className={`w-4 h-px ${c.deliveredAt ? 'bg-emerald-500' : 'bg-muted-foreground/20'}`} />
                      <div className={`w-1.5 h-1.5 rounded-full ${c.deliveredAt ? 'bg-emerald-500' : 'bg-muted-foreground/20'}`} title={tipDelivered} />
                      <div className={`w-4 h-px ${c.readAt ? 'bg-violet-500' : 'bg-muted-foreground/20'}`} />
                      <div className={`w-1.5 h-1.5 rounded-full ${c.readAt ? 'bg-violet-500' : 'bg-muted-foreground/20'}`} title={tipRead} />
                      <span className="text-[8px] text-muted-foreground/50 ml-1">
                        {c.readAt ? fmtDateTime(c.readAt) : c.deliveredAt ? fmtDateTime(c.deliveredAt) : ''}
                      </span>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        )}

        {/* ─── LEADS ENCAMINHADOS ─── Só aparece se o contato é líder COM leads
             pendentes de baixa (self-gating pelo endpoint by-leader). Fallback
             manual: status + comentário + botões de baixa, sem sair da conversa. */}
        {leaderCounts && leaderCounts.pending > 0 && (
          <>
            <button onClick={() => toggleSection('leads')} className="w-full flex items-center gap-2 px-4 py-2 text-[11px] font-semibold text-muted-foreground hover:bg-secondary/50 transition-colors border-b border-border/50">
              {expandedSections.has('leads') ? <FiChevronDown className="w-3 h-3" /> : <FiChevronRight className="w-3 h-3" />}
              <FiAward className={`w-3 h-3 ${neu ? 'text-muted-foreground' : 'text-purple-500'}`} /> LEADS ENCAMINHADOS
              <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded-full tabular-nums ${neu ? 'neu-chip' : 'bg-purple-500/15 text-purple-600 dark:text-purple-400'}`}>{leaderCounts.pending} p/ baixa</span>
            </button>
            {expandedSections.has('leads') && (
              <div className="px-3 py-2 space-y-2 border-b border-border/50">
                {/* Ações sobre a LÍDER (bloquear / transferir tudo) — admin-only,
                    gate dentro do próprio componente. Fica no topo da seção
                    porque age sobre o conjunto, não sobre um lead da lista. */}
                {profile?.code && (
                  <LeaderActionsBar
                    neu={neu}
                    code={profile.code}
                    leaderName={profile.name || contactName || 'esta líder'}
                    pending={leaderCounts.pending}
                    onChanged={() => { if (profile.code) loadLeaderLeads(profile.code) }}
                  />
                )}
                {leaderLeadsLoading && (
                  <div className="flex items-center justify-center gap-2 py-2 text-[11px] text-muted-foreground">
                    <FiLoader className="w-3.5 h-3.5 animate-spin" /> Atualizando…
                  </div>
                )}
                {leadError && (
                  <div className="text-[10px] text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
                    <FiAlertCircle className="w-3 h-3 shrink-0" /> {leadError}
                  </div>
                )}
                {leadInfo && (
                  <div className="text-[10px] text-sky-600 dark:text-sky-400 flex items-center gap-1.5">
                    <FiSend className="w-3 h-3 shrink-0" /> {leadInfo}
                  </div>
                )}
                {leaderLeads.filter(l => showConcludedLeads || l.status !== 'concluded').map(l => {
                  const sm = LEAD_STATUS_META[l.status]
                  const action = nextLeadAction(l)
                  const busy = leadActionBusy === l.assignmentId
                  return (
                    <div key={l.assignmentId} className={`px-2.5 py-2 ${neu ? 'neu-card' : 'rounded-md border border-border bg-card'}`}>
                      <div className="flex items-center gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-[12px] font-medium text-foreground truncate">{l.leadName}</div>
                          <div className="text-[10px] text-muted-foreground truncate">{l.leadPhone}</div>
                        </div>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${sm.cls}`}>{sm.label}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-[10px]">
                        {/* Próxima ação automática enquanto aguarda retorno (open/overdue):
                            lembrete 12h → aviso de prazo 24h → expirado. Quando a líder já
                            respondeu "tentando" (acknowledged), o prazo não corre mais. */}
                        {action && (
                          <span className={`inline-flex items-center gap-1 tabular-nums ${action.cls}`}
                            title={l.deadlineAt ? `Prazo final: ${new Date(l.deadlineAt).toLocaleString('pt-BR', { timeZone: 'America/Campo_Grande' })}` : ''}>
                            <FiClock className="w-3 h-3" />{action.text}
                          </span>
                        )}
                        {l.convertedOracle && <span className="text-purple-600 dark:text-purple-400 font-bold">✦ Convertido</span>}
                        {l.nudgeCount > 0 && <span className="text-muted-foreground/70">{l.nudgeCount} lembrete{l.nudgeCount === 1 ? '' : 's'}</span>}
                      </div>
                      {/* Funil 3 etapas (v2) + registro manual por etapa — substituiu os
                          botões de desfecho único "Deu certo/Não deu/Tentando" (2026-07-10),
                          que gravavam o derivado cru e deixavam o funil órfão. */}
                      <div className="mt-2">
                        <LeaderStageControl
                          neu={neu}
                          f={{ stage: l.stage, contacted: l.contacted, explained: l.explained, interest: l.interest }}
                          status={l.status} nudgeCount={l.nudgeCount}
                          leaderOutcome={l.leaderOutcome} leaderOutcomeReason={l.leaderOutcomeReason}
                          busy={busy}
                          onReply={(reply: StageReply) => leadAction(l.assignmentId, 'stage-manual', { reply })}
                        />
                      </div>
                      {l.status !== 'concluded' ? (
                        <div className="mt-2 space-y-1.5">
                          <div className="grid grid-cols-2 gap-1">
                            {l.autoExpiry && l.status === 'overdue' ? (
                              <button disabled={busy}
                                onClick={() => leadAction(l.assignmentId, 'notify-expired', {})}
                                title="Avisar a líder que o prazo de 24h expirou (template de utilidade). Pinga a líder e sobe a conversa no topo do inbox."
                                className={`h-7 text-[10px] font-medium rounded-md disabled:opacity-40 transition-colors inline-flex items-center justify-center gap-1 bg-amber-500/10 text-amber-600 dark:text-amber-400 hover:bg-amber-500/20 ${neu ? 'neu-relief' : 'border border-amber-500/40'}`}>
                                <FiClock className="w-3 h-3" /> Notificar expirado
                              </button>
                            ) : (
                              <button disabled={busy}
                                onClick={() => leadAction(l.assignmentId, 'resend-nudge', {})}
                                title="Reenviar a pergunta da etapa atual à líder pelo WhatsApp. Pinga a líder e sobe a conversa no topo do inbox."
                                className={`h-7 text-[10px] font-medium rounded-md disabled:opacity-40 transition-colors inline-flex items-center justify-center gap-1 bg-sky-500/10 text-sky-600 dark:text-sky-400 hover:bg-sky-500/20 ${neu ? 'neu-relief' : 'border border-sky-500/40'}`}>
                                <FiSend className="w-3 h-3" /> Reenviar cobrança
                              </button>
                            )}
                            <button disabled={busy}
                              onClick={() => setReassignLead(l)}
                              title="Encaminhar este lead a OUTRA líder. Reinicia o relógio de 24h para a nova líder."
                              className={`h-7 text-[10px] font-medium rounded-md disabled:opacity-40 transition-colors inline-flex items-center justify-center gap-1 bg-purple-500/10 text-purple-600 dark:text-purple-400 hover:bg-purple-500/20 ${neu ? 'neu-relief' : 'border border-purple-500/40'}`}>
                              <FiRepeat className="w-3 h-3" /> Trocar líder
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )
                })}
                {leaderCounts.concluded > 0 && (
                  <button onClick={() => setShowConcludedLeads(v => !v)} className="text-[10px] text-muted-foreground hover:text-foreground underline">
                    {showConcludedLeads ? 'Ocultar concluídos' : `Ver ${leaderCounts.concluded} concluído${leaderCounts.concluded === 1 ? '' : 's'}`}
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {/* ─── BLOQUEIO ─── Atalho pro mesmo contact_blocklist do painel
            Recrutamento → Bloqueados (pedido 2026-07-16): quem descobre o golpe
            está lendo a conversa, não navegando em painel. Admin-only porque a
            rota é adminMiddleware — botão visível pra não-admin só daria 403.
            Colapsada por default: é ação destrutiva, não compete com os dados
            do lead. */}
        {user?.isAdmin && phone && (
          <>
            <button onClick={() => toggleSection('block')} className="w-full flex items-center gap-2 px-4 py-2 text-[11px] font-semibold text-muted-foreground hover:bg-secondary/50 transition-colors border-b border-border/50">
              {expandedSections.has('block') ? <FiChevronDown className="w-3 h-3" /> : <FiChevronRight className="w-3 h-3" />}
              <FiSlash className={`w-3 h-3 ${blocked ? 'text-red-500' : ''}`} /> BLOQUEIO
              {blocked && (
                <span className="ml-auto text-[9px] bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/40 px-1.5 py-0.5 rounded-full font-bold">
                  Bloqueado
                </span>
              )}
            </button>
            {expandedSections.has('block') && (
              <div className="px-4 py-2 space-y-2 border-b border-border/50">
                {blocked ? (
                  <>
                    <div className="text-[11px] text-red-600 dark:text-red-400 leading-relaxed">
                      A Jú não responde este contato e ele não pode ser encaminhado a nenhuma líder.
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      <span className="uppercase tracking-wider">Motivo</span>
                      <div className="text-foreground text-[11px]">{blocked.reason || '—'}</div>
                    </div>
                    {blocked.blocked_by && (
                      <div className="text-[10px] text-muted-foreground/70">
                        Bloqueado por {blocked.blocked_by}
                        {blocked.blocked_at ? ` em ${new Date(
                          blocked.blocked_at.includes('T') ? blocked.blocked_at : blocked.blocked_at.replace(' ', 'T') + 'Z'
                        ).toLocaleDateString('pt-BR')}` : ''}
                      </div>
                    )}
                    <div className="text-[10px] text-muted-foreground/70 pt-1 border-t border-border/40">
                      Para desbloquear, use Recrutamento → Bloqueados.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-[10px] text-muted-foreground leading-relaxed">
                      Bloquear faz a Jú parar de responder e impede o encaminhamento a qualquer
                      líder — inclusive manual. As mensagens continuam chegando aqui, marcadas.
                    </div>
                    {blockError && (
                      <div className="px-2 py-1.5 rounded bg-destructive/10 text-destructive text-[10px]">{blockError}</div>
                    )}
                    <input
                      value={blockReason}
                      onChange={e => setBlockReason(e.target.value)}
                      placeholder="Motivo (obrigatório) — ex: aplica golpes"
                      title="O motivo fica registrado na lista para auditoria"
                      className="w-full px-2 py-1.5 rounded-lg bg-background border border-border text-[11px] text-foreground placeholder:text-muted-foreground"
                    />
                    <button
                      onClick={() => setConfirmBlock(true)}
                      disabled={blocking || !blockReason.trim()}
                      title={!blockReason.trim() ? 'Informe o motivo do bloqueio' : 'Bloquear este contato'}
                      className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 transition-colors"
                    >
                      {blocking ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiSlash className="w-3 h-3" />}
                      {blocking ? 'Bloqueando…' : 'Bloquear contato'}
                    </button>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmBlock}
        type="danger"
        title="Bloquear este contato?"
        message={`${contactName || 'Este contato'} deixa de ser atendido pela Jú e não poderá ser encaminhado a nenhuma líder, nem manualmente.`}
        details={[`Motivo: ${blockReason}`, 'Reversível em Recrutamento → Bloqueados.']}
        confirmText="Bloquear"
        cancelText="Cancelar"
        onConfirm={() => { setConfirmBlock(false); blockContact() }}
        onCancel={() => setConfirmBlock(false)}
      />

      {/* Trocar líder a partir da sidebar: reaproveita o mesmo modal do inbox/painel.
          conversationId/deviceId vêm do lead (não da líder em foco). */}
      {reassignLead && (
        <AssignLeaderModal
          isOpen={!!reassignLead}
          onClose={() => setReassignLead(null)}
          conversationId={reassignLead.conversationId}
          deviceId={reassignLead.deviceId}
          contactName={reassignLead.leadName}
          contactPhone={reassignLead.leadPhone}
          onAssign={() => { setReassignLead(null); if (profile?.code) loadLeaderLeads(profile.code) }}
        />
      )}
    </div>
  )
}
